import { ferryFreeBlocks } from '@my/ferry-api';
import { useFerryCapacity, useFerrySailings, useFerryVehicleTypes } from '@my/ferry-api/hooks';
import type { BookingTypeValue } from '@my/tms-api';
import { cn } from '@my/ui';
import { useEffect, useMemo, useRef } from 'react';
import { roadMinutesFor } from '../../../utils/roadDistance';
import type { CrossingCarrier, CrossingRoute } from '../../ferry/catalogue';
import { formatPortTime, zonedToUtc } from '../../ferry/portTime';
import { useFerryCrossing } from '../../ferry/useFerryCrossing';
import { defaultVehicleType } from '../../ferry/vehicleType';
import {
  absMinutesFromIso,
  buildSampleSailings,
  fmtDur,
  fmtHM,
  isoDateFromAbsMinutes,
  nowAbsMinutes,
  roadToDestText,
  type BookingContext,
  type CandidateLeg,
  type SailingRow,
  type SailingRowBase,
} from '../lib/bookingDetail';
import type { BookingRow } from '../lib/mockData';
import { SailingTypePill } from './SailingTypePill';

const CUSTOMS_CLEAR_MIN = 45;

/**
 * The sailing rows of one candidate crossing, read from the ferry service when
 * the crossing is booked through it: every sailing between now and the
 * delivery deadline, in the port's clock, with the organisation's free blocks
 * and whether the truck can still make it. A crossing the integration has no
 * data for (no account at the carrier, the integration off, the service
 * unreachable, a carrier it does not know, or no timetable held) shows the
 * design's sample sailings instead, booked in the TMS only — nothing here
 * blocks a TMS booking. Type and cost stay the placeholders until a rating
 * source exists.
 */
export function SailingOptions({
  orgId,
  row,
  leg,
  entry,
  carrier,
  ctx,
  bookingType,
  accompanied,
  integrationOn,
  selectedId,
  onSelect,
  onDeselect,
  index,
}: {
  orgId: string | null;
  row: BookingRow;
  leg: CandidateLeg;
  entry: CrossingRoute;
  carrier: CrossingCarrier | null;
  ctx: BookingContext;
  bookingType: BookingTypeValue;
  accompanied: boolean;
  integrationOn: boolean;
  selectedId: string | null;
  onSelect: (sailing: SailingRow) => void;
  onDeselect: () => void;
  /** Position in the panel: the first is the recommended crossing, badged
   *  Preferred, and it picks its first viable sailing when nothing is selected. */
  index: number;
}) {
  const recommended = index === 0;
  const crossing = useFerryCrossing(orgId, entry, leg.candidate.reversed, carrier, integrationOn);
  const active = integrationOn && crossing.enabled;
  const zone = crossing.originZone;

  // Today up to the end of the delivery day, in the departure port's clock.
  const window = useMemo(() => {
    const lastDay = Math.max(0, row.deliveryDayOffset) + 1;
    return {
      from: zonedToUtc(isoDateFromAbsMinutes(0), '00:00', zone),
      to: zonedToUtc(isoDateFromAbsMinutes(lastDay * 1440), '00:00', zone),
    };
  }, [row.deliveryDayOffset, zone]);

  const sailingsQuery = useFerrySailings(
    active ? crossing.carrierId : null,
    active ? crossing.routeCode : null,
    window.from,
    window.to,
  );
  const capacityQuery = useFerryCapacity(
    active ? orgId : null,
    crossing.carrierId,
    crossing.routeCode,
    window.from,
    window.to,
  );
  const typesQuery = useFerryVehicleTypes(active ? crossing.carrierId : null, true);

  // The carrier's type for this load, the way the order form derives it.
  const derived = useMemo(
    () =>
      defaultVehicleType({
        map: crossing.vehicleTypes,
        equipmentKeys: row.equipmentKeys ?? [],
        bookingType,
        accompanied,
        reefer: row.temperatureC != null,
        types: typesQuery.data ?? [],
      }),
    [
      crossing.vehicleTypes,
      row.equipmentKeys,
      row.temperatureC,
      bookingType,
      accompanied,
      typesQuery.data,
    ],
  );

  const rows = useMemo<SailingRow[]>(() => {
    const option = leg.option;
    const departurePort = option.departurePort ?? ctx.depPort;
    const base: SailingRowBase = {
      routeLabel: `${departurePort} to ${option.arrivalPort}`,
      departurePort,
      arrivalPort: option.arrivalPort,
      roadKm: option.roadKm,
      roadMinutes: option.roadMinutes,
      roadToDest: roadToDestText(option.roadKm, option.roadMinutes, ctx.destination),
      preferred: recommended,
      operator: option.operator,
      type: option.type,
      blockSub: '',
      cost: option.cost,
      fuel: ctx.baseFuel,
      checkInMin: entry.typicalCheckInMinutes ?? ctx.checkInMin,
      candidate: { ...leg.candidate, entry, carrier },
    };

    const timeless = (statusText: string, viable: boolean): SailingRow => ({
      ...base,
      id: `${leg.key}:manual`,
      departs: '—',
      arrives: '—',
      duration: '—',
      blockLabel: '',
      viable,
      statusText,
      ferry: null,
    });

    const sample = () =>
      buildSampleSailings(base, leg.key, index, entry.typicalDurationMinutes ?? 4 * 60);

    if (!active) {
      if (crossing.isLoading) return [timeless('Checking with the ferry service…', false)];
      return sample();
    }
    if (sailingsQuery.isPending) return [timeless('Checking the timetable…', false)];
    if (sailingsQuery.isError) return sample();

    const sailings = (sailingsQuery.data ?? [])
      .filter((s) => s.status !== 'Cancelled')
      .sort((a, b) => a.scheduledDeparture.localeCompare(b.scheduledDeparture));
    if (sailings.length === 0) return sample();

    const capacityByDeparture = new Map(
      (capacityQuery.data?.sailings ?? []).map((s) => [s.scheduledDeparture, s]),
    );
    const nowAbs = nowAbsMinutes();
    const drive = option.roadMinutes ?? roadMinutesFor(option.roadKm);
    const customsMin = ctx.customs ? CUSTOMS_CLEAR_MIN : 0;
    const vehicleType = typesQuery.data?.find((t) => t.code === derived.code) ?? null;

    const built = sailings.map((s): SailingRow => {
      const departsAtMin = absMinutesFromIso(s.scheduledDeparture);
      const arrivesAtMin = s.scheduledArrival
        ? absMinutesFromIso(s.scheduledArrival)
        : departsAtMin + (entry.typicalDurationMinutes ?? 4 * 60);
      const catchable = departsAtMin - base.checkInMin > nowAbs;
      const onTime = arrivesAtMin + customsMin + drive <= ctx.deliveryDeadlineMin;
      const viable = catchable && onTime;
      // No blocks held on the sailing (no capacity row, or none free) means the
      // booking takes open-market space; the column is never blank.
      const cap = capacityByDeparture.get(s.scheduledDeparture);
      const free = cap ? ferryFreeBlocks(cap, 'Standard') + ferryFreeBlocks(cap, 'High') : 0;
      return {
        ...base,
        id: s.id,
        departs: formatPortTime(s.scheduledDeparture, zone),
        arrives: s.scheduledArrival
          ? formatPortTime(s.scheduledArrival, crossing.destinationZone)
          : fmtHM(arrivesAtMin),
        arrivesNote: viable ? undefined : 'Not viable',
        duration: fmtDur(Math.max(0, arrivesAtMin - departsAtMin)),
        blockLabel: free > 0 ? `Blk: ${free}` : 'Open mkt',
        blockSub: `${free} off-pk`,
        viable,
        statusText: !catchable
          ? 'Collection cannot reach port before cutoff'
          : !onTime
            ? 'Arrives after the delivery window'
            : 'On schedule',
        departsAtMin,
        arrivesAtMin,
        ferry: {
          carrierId: crossing.carrierId ?? '',
          routeCode: crossing.routeCode ?? '',
          originZone: zone,
          destinationZone: crossing.destinationZone,
          credentials: crossing.credentials,
          credentialRequired: crossing.credentialRequired,
          credentialId: crossing.credentialRequired ? '' : (crossing.credentials[0]?.id ?? ''),
          sailingUtc: s.scheduledDeparture,
          arrivalUtc: s.scheduledArrival ?? null,
          vehicleTypeCode: derived.code,
          lengthMetres: derived.lengthMetres,
          vehicleType,
        },
      };
    });
    // The last sailing the truck can still make and deliver on time.
    const lastViable = [...built].reverse().find((r) => r.viable);
    if (lastViable) lastViable.departsNote = 'Latest viable';
    return built;
  }, [
    leg,
    entry,
    carrier,
    ctx,
    index,
    recommended,
    active,
    zone,
    crossing,
    sailingsQuery.data,
    sailingsQuery.isPending,
    sailingsQuery.isError,
    capacityQuery.data,
    typesQuery.data,
    derived,
  ]);

  // The recommended crossing starts selected on its first viable sailing, as
  // the panel always opened with the best route picked.
  useEffect(() => {
    if (!recommended || selectedId) return;
    const first = rows.find((r) => r.viable);
    if (first) onSelect(first);
  }, [recommended, selectedId, rows, onSelect]);

  // A refreshed table re-emits the selected row's current copy (its vehicle
  // type may arrive after the sailings) and drops a selection whose sailing is
  // gone, for the crossing that owned it.
  const ownedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (selectedId) {
      const fresh = rows.find((r) => r.id === selectedId);
      if (fresh) onSelect(fresh);
      else if (ownedIds.current.has(selectedId)) onDeselect();
    }
    ownedIds.current = new Set(rows.map((r) => r.id));
  }, [rows, selectedId, onSelect, onDeselect]);

  return (
    <>
      {rows.map((s) => {
        const isSel = s.id === selectedId;
        return (
          <tr
            key={s.id}
            onClick={() => s.viable && onSelect(s)}
            className={cn(
              s.viable ? 'cursor-pointer hover:bg-slate-50' : 'opacity-50',
              isSel && 'bg-blue-50',
            )}
          >
            <td className="px-2 py-2 align-top">
              <input
                type="radio"
                name="sailing"
                checked={isSel}
                disabled={!s.viable}
                onChange={() => onSelect(s)}
                aria-label={`Select ${s.routeLabel} ${s.departs}`}
              />
            </td>
            <td className="px-2 py-2 align-top">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-800">{s.routeLabel}</span>
                {s.preferred && (
                  <span className="rounded bg-blue-50 px-1 text-[9px] font-medium text-blue-600">
                    Preferred
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-400">{s.roadToDest}</div>
            </td>
            <td className="px-2 py-2 align-top text-slate-600">{s.operator}</td>
            <td className="px-2 py-2 align-top">
              <div className="font-semibold tabular-nums text-slate-800">{s.departs}</div>
              {s.departsNote && (
                <div className="text-[10px] font-medium text-emerald-600">{s.departsNote}</div>
              )}
            </td>
            <td className="px-2 py-2 align-top">
              <div className="tabular-nums text-slate-700">{s.arrives}</div>
              {s.arrivesNote && (
                <div className="text-[10px] font-medium text-red-500">{s.arrivesNote}</div>
              )}
            </td>
            <td className="px-2 py-2 align-top tabular-nums text-slate-500">{s.duration}</td>
            <td className="px-2 py-2 align-top">
              <SailingTypePill type={s.type} />
            </td>
            <td className="px-2 py-2 align-top">
              <div
                className={cn(
                  'font-medium',
                  s.blockLabel.startsWith('Blk') ? 'text-emerald-600' : 'text-amber-600',
                )}
              >
                {s.blockLabel}
              </div>
              <div className="text-[10px] text-slate-400">{s.blockSub}</div>
            </td>
            <td className="px-2 py-2 align-top">
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11px]',
                  s.viable ? 'text-slate-500' : 'text-red-600',
                )}
              >
                {s.viable && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                {s.statusText}
              </span>
            </td>
          </tr>
        );
      })}
    </>
  );
}
